// Team PLAYER individual report (v6.5, 2026-05-24).
//
// Replaces the v6.2 minimal table-only version with the full report from
// the 2026-05-19 Psychology team meeting: Section A header + PLAHER radar
// chart + Section B (peer picture, self-other, shape, feedback quality) +
// Section C (themes with Justification / Recommendations split, PLAHER
// dimensions in bold) + Section D (next direction) + Section E (strengths).
// The existing minimal table is preserved as a "Detailed scores" section
// at the bottom; peer feedback comments remain below that.
//
// The AI-synthesized sections are lazy-fetched from
// GET /api/survey/:surveyId/report/individual/:respondentId. First view
// triggers the 3-pass Anthropic pipeline (~15s) and caches the result;
// subsequent views are instant. An admin "Regenerate" button is available
// when the report has been cached.

import React, { useEffect, useState } from 'react';
import {
  Alert, Card, Spinner, Table,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { getIndividualReport } from '../../../bridge';
import { logError, logEvent } from '../../../helpers/logger';

import { PlaherRadarChart } from './PlaherRadarChart';

const BAR_MAX = 5;

// Inline markdown bold renderer: turns `**text**` into <strong>text</strong>.
// Synthesis output marks PLAHER dimension names in bold inside theme bodies.
function renderBold(text) {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      // eslint-disable-next-line react/no-array-index-key
      return <strong key={`bold-${i}`}>{p.slice(2, -2)}</strong>;
    }
    // eslint-disable-next-line react/no-array-index-key
    return <span key={`txt-${i}`}>{p}</span>;
  });
}

function MiniBar({ value }) {
  const num = Number.isFinite(value) ? value : null;
  const pct = num !== null ? Math.max(0, Math.min(100, (num / BAR_MAX) * 100)) : 0;
  return (
    <div
      style={{
        display: 'inline-block',
        width: 120,
        height: 10,
        background: '#e9ecef',
        borderRadius: 4,
        verticalAlign: 'middle',
        marginRight: 8,
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: '#5b9bd5',
          borderRadius: 4,
        }}
      />
    </div>
  );
}
MiniBar.propTypes = { value: PropTypes.number };
MiniBar.defaultProps = { value: null };

function fmt(v) {
  return (v === null || v === undefined) ? 'N/A' : String(v);
}

function gap(peer, group) {
  if (peer === null || group === null) return '';
  const d = peer - group;
  if (Math.abs(d) < 0.05) return '(at team avg)';
  return d > 0 ? `(+${d.toFixed(1)} vs team avg)` : `(${d.toFixed(1)} vs team avg)`;
}

function SectionParagraph({ heading, text }) {
  if (!text) return null;
  return (
    <div className="mb-3">
      {heading ? <h5 className="mb-2">{heading}</h5> : null}
      <p style={{ marginBottom: 0 }}>{renderBold(text)}</p>
    </div>
  );
}
SectionParagraph.propTypes = { heading: PropTypes.string, text: PropTypes.string };
SectionParagraph.defaultProps = { heading: '', text: '' };

function ThemeBlock({ theme, index }) {
  if (!theme) return null;
  const title = theme.title || `Theme ${index + 1}`;
  const justification = theme.justification || theme.body || '';
  const recommendations = theme.recommendations || '';
  return (
    <div className="mb-4 ps-3 border-start border-3" style={{ borderColor: '#5b9bd5' }}>
      <h5 className="mb-2">{`${index + 1}. ${title}`}</h5>
      {justification ? (
        <div className="mb-2">
          <div className="text-muted small mb-1" style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Justification
          </div>
          <div>{renderBold(justification)}</div>
        </div>
      ) : null}
      {recommendations ? (
        <div>
          <div className="text-muted small mb-1" style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Recommendations
          </div>
          <div>{renderBold(recommendations)}</div>
        </div>
      ) : null}
    </div>
  );
}
ThemeBlock.propTypes = {
  theme: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    justification: PropTypes.string,
    recommendations: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

// Main section component.
//
// Strategy:
//   - Always render the deterministic "Detailed scores" table + peer
//     comments (works even when AI is disabled / errors).
//   - Lazy-fetch the AI synthesis on mount. While loading, show an inline
//     spinner with a "this can take up to 30 seconds the first time"
//     hint. On error, show an Alert; the deterministic sections still
//     render below.
//   - On admin regenerate click, POST to the regenerate route and re-
//     populate state.
export function ReportTeamPlayerSummarySection({ respondent }) {
  const [synthesis, setSynthesis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const surveyId = respondent && respondent.surveyId;
  const respondentId = respondent && respondent.respondentId;

  useEffect(() => {
    if (!surveyId || !respondentId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrMsg('');
    logEvent('individual_report_fetch', { surveyId, respondentId });
    getIndividualReport(surveyId, respondentId)
      .then(({ response, error: networkError }) => {
        if (cancelled) return;
        if (networkError) {
          setErrMsg(`Unable to load the report (${typeof networkError === 'string' ? networkError : 'server error'}).`);
          return;
        }
        if (!response || typeof response !== 'object') {
          setErrMsg('No response from server.');
          return;
        }
        // v6.5.3 (2026-05-24): always store the synthesis response, even when
        // .report is null. The response carries hostile-filtered signals
        // (peer_means, qualitative_comments_for_synthesis, n_voices, etc.)
        // that the radar + Detailed scores + Peer feedback comments render
        // from. Storing it unconditionally means the recipient never sees the
        // unfiltered teamPlayerCalc fallback after the API has responded.
        setSynthesis(response);
        if (!response.report) {
          if (response.error === 'no_peer_voices') {
            setErrMsg('No usable peer voices for this respondent (all raters were excluded by the safety filter, or no peer ratings were submitted).');
          } else if (response.error) {
            setErrMsg(`AI report synthesis was unavailable for this respondent (${response.error}). The hostile-filtered peer ratings and any non-hostile comments are still shown below.`);
          } else {
            setErrMsg('AI report synthesis is not yet available. The hostile-filtered peer ratings are shown below.');
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        logError('individual_report_fetch', err);
        setErrMsg('Unable to load the report. Please refresh and try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [surveyId, respondentId]);

  const r = respondent && respondent.report;
  if (!r) return null;
  const {
    ratings, dimensionsOrder, qualitative = [], teamMeta = {},
  } = r;
  const firstName = (r.respondent && r.respondent.firstName) || respondent.firstName || '';
  const lastName = (r.respondent && r.respondent.lastName) || respondent.lastName || '';
  const team = (r.respondent && r.respondent.team) || respondent.team || '';

  const synthesisReport = synthesis && synthesis.report;
  const signals = synthesis && synthesis.signals;
  const nVoices = signals ? signals.n_voices : (teamMeta.peerCount || 0);
  const nExcluded = signals ? signals.n_excluded_hostile : 0;

  return (
    <Card className="pagebreak p-4 py-3 my-3">
      <Card.Body>
        {/* Section A — header */}
        <div className="mb-4 d-flex justify-content-between align-items-start">
          <div>
            <h2>
              {signals && signals.profile_id === 'ent'
                ? 'Leading Entrepreneurial Teams Report'
                : 'Team PLAYER Report'}
              {firstName ? ` — ${firstName}` : ''}
              {lastName ? ` ${lastName}` : ''}
            </h2>
            {/* v6.5.5 (2026-05-24): silent hostile-rater filtering. The
                recipient sees only the non-hostile rater count -- never
                a disclosure that some raters were filtered as hostile.
                Surfacing that information here tells the recipient a
                teammate submitted a hostile comment about them, which
                is harmful. The admin Survey Report Summary surfaces the
                same information non-anonymously via the dedicated
                HostileCommentsAdmin card, where it belongs. */}
            <p className="text-muted mb-0">
              {team ? `Team ${team}. ` : ''}
              {`Based on ${nVoices} peer rater${nVoices === 1 ? '.' : 's.'}`}
            </p>
          </div>
          {/* Regenerate button removed from the recipient-facing report view
              (2026-07-01, Denis: globally remove so participants can't trigger a
              fresh AI synthesis). The admin regenerate route stays (audit-logged);
              the admin surface triggers it, not this recipient card. */}
        </div>

        {/* Radar chart, rendered as soon as signals are available (does not
            wait for the full synthesis to finish). */}
        {signals ? (
          <div className="mb-4 d-flex justify-content-center">
            <PlaherRadarChart signals={signals} />
          </div>
        ) : null}

        {/* Loading state for first-time generation */}
        {loading && !synthesisReport ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status" className="me-2" />
            <span>Generating your individual report. This can take up to 30 seconds the first time.</span>
          </div>
        ) : null}

        {errMsg ? (
          <Alert variant="warning" className="my-3">
            {errMsg}
          </Alert>
        ) : null}

        {/* Section B — interpretation paragraphs */}
        {synthesisReport ? (
          <>
            <h4 className="mb-3 mt-2">Your peers&apos; view</h4>
            <SectionParagraph text={synthesisReport.section_B_peer_picture} />
            <SectionParagraph heading="Self vs peer view" text={synthesisReport.section_B_self_other} />
            <SectionParagraph heading="Shape of the profile" text={synthesisReport.section_B_shape} />
            <SectionParagraph heading="Signal triangulation" text={synthesisReport.section_B_feedback_quality} />

            {/* Section C — themes */}
            {Array.isArray(synthesisReport.section_C_themes) && synthesisReport.section_C_themes.length ? (
              <>
                <h4 className="mb-3 mt-4">Themes</h4>
                {synthesisReport.section_C_themes.map((t, idx) => (
                  <ThemeBlock
                    key={`theme-${idx}-${(t.title || '').slice(0, 16)}`}
                    theme={t}
                    index={idx}
                  />
                ))}
              </>
            ) : null}

            {/* Section D — next direction */}
            {synthesisReport.section_D_next_direction ? (
              <>
                <h4 className="mb-3 mt-4">Next direction</h4>
                <p>{renderBold(synthesisReport.section_D_next_direction)}</p>
              </>
            ) : null}

            {/* Section E — strengths */}
            {Array.isArray(synthesisReport.section_E_strengths) && synthesisReport.section_E_strengths.length ? (
              <>
                <h4 className="mb-3 mt-4">Strengths</h4>
                <ul>
                  {synthesisReport.section_E_strengths.map((s, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`strength-${idx}`} className="mb-2">{renderBold(s)}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}

        {/* Detailed scores and peer feedback are rendered ONLY from
            hostile-filtered signals. Never fall back to the unfiltered
            teamPlayerCalc output -- doing so leaks bad-faith Likert and
            qualitative content into the recipient's view (v6.5.3 fix).
            When signals is null (transient API error), show a placeholder
            instead of leaking. */}
        <h4 className="mt-5 mb-3">Detailed scores</h4>
        {signals && signals.peer_means ? (
          <>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th style={{ width: 280 }}>Peer rating (1-5)</th>
                  <th>Raters</th>
                </tr>
              </thead>
              <tbody>
                {(dimensionsOrder || Object.keys(signals.peer_means)).map((dim) => {
                  const val = signals.peer_means[dim];
                  return (
                    <tr key={dim}>
                      <td><strong>{(signals.dim_labels && signals.dim_labels[dim]) || dim}</strong></td>
                      <td>
                        <MiniBar value={val} />
                        <span style={{ verticalAlign: 'middle' }}>{fmt(val)}</span>
                      </td>
                      <td>{signals.n_voices}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {/* v6.5.5: do not disclose hostile-rater exclusions to the
                recipient. The admin sees the same data via the dedicated
                HostileCommentsAdmin card on the Survey Report Summary. */}
          </>
        ) : (
          <p className="text-muted">Detailed scores are being prepared. Refresh the page in a moment.</p>
        )}

        <h4 className="mt-4 mb-3">Peer feedback comments</h4>
        {signals && Array.isArray(signals.qualitative_comments_for_synthesis) ? (
          <>
            {signals.qualitative_comments_for_synthesis.length === 0 ? (
              <p className="text-muted">Your teammates did not leave qualitative feedback (or any comments left were flagged hostile and excluded).</p>
            ) : (
              signals.qualitative_comments_for_synthesis.map((text, idx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`teamplayer-comment-${idx}`}
                  className="border-start border-3 ps-3 my-3 fst-italic"
                >
                  {text}
                </div>
              ))
            )}
            {/* v6.5.5: hostile-flagged comments are silently dropped here.
                The admin Survey Report Summary surfaces the full list via
                HostileCommentsAdmin. */}
          </>
        ) : (
          <p className="text-muted">Peer feedback comments are being prepared. Refresh the page in a moment.</p>
        )}

        {/* Restored 2026-05-24 (Denis): consistent with the platform's
            built-in confidentialText feature (see server/src/db/defaults/
            teamPlayer.js -- students can submit instructor-only comments
            alongside their public peer feedback). The footer is a factual
            statement about the platform feature, not a hostility note. */}
        <p className="text-muted small mt-4">
          Confidential comments your teammates submitted are not shown here;
          they are only visible to your instructor.
        </p>
      </Card.Body>
    </Card>
  );
}

ReportTeamPlayerSummarySection.propTypes = {
  respondent: PropTypes.shape({
    respondentId: PropTypes.string,
    surveyId: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    team: PropTypes.string,
    report: PropTypes.object,
  }),
};

ReportTeamPlayerSummarySection.defaultProps = {
  respondent: { report: null },
};

export default ReportTeamPlayerSummarySection;
